import {
  faAnglesDown,
  faAnglesUp,
  faArrowDown,
  faArrowUp,
  faPencil,
  faTrash,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { type FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useFetchedResource } from "../../api/APIContext";
import { useApiCall } from "../../api/api";
import type { DefaultInspectionAttachment } from "../../api/dto";
import type { InspectionAttachment } from "../../api/dto/InspectionAttachment";
import { AddInspectionAttachmentsModal } from "../../modals/AddInspectionAttachmentsModal";
import { DeleteAttachmentModal } from "../../modals/DeleteAttachmentModal";
import { EditAttachmentName } from "../../modals/EditAttachmentName";
import { Card } from "../Card";
import { Badge, BadgeStyle } from "../data/Badge";
import { Button, IconButton } from "../form/Button";
import { Loading } from "../Loading";
import { useModal } from "../layout/ModalProvider";
import { useRefreshEffect } from "../RefreshController";
import type { EnrichedInspection } from "./types";

export const InspectionAttachmentsOverview: FC<{
  inspection: EnrichedInspection;
  isCustomer: boolean;
}> = (props) => {
  const { listAttachments, listDefaultInspectionAttachments, updateAttachmentOrder } = useApiCall();
  const attachmentsResource = useFetchedResource((inspectionId: string) =>
    listAttachments(inspectionId),
  );
  const defaultAttachmentResource = useFetchedResource(() => listDefaultInspectionAttachments());
  const [attachments, setAttachments] = useState<InspectionAttachment[]>([]);
  const [defaultAttachments, setDefaultAttachments] = useState<DefaultInspectionAttachment[]>([]);
  useRefreshEffect(() => {
    attachmentsResource.reload(props.inspection.id);
    defaultAttachmentResource.reload(undefined);
  });
  useEffect(() => {
    if (attachmentsResource.resource !== null) {
      setAttachments(attachmentsResource.resource?.sort((a, b) => a.order - b.order));
    }
    if (defaultAttachmentResource.resource !== null) {
      setDefaultAttachments(
        defaultAttachmentResource.resource
          ?.filter((da) => da.isActive)
          .sort((a, b) => a.order - b.order),
      );
    }
  }, [attachmentsResource.resource, defaultAttachmentResource.resource]);

  const hasLocalOrderChanges = useMemo(() => {
    const remoteItems = [...(attachmentsResource.resource ?? [])];
    const remoteOrder = remoteItems.sort((a, b) => a.order - b.order).map((p) => p.id);
    const localItems = [...attachments];
    const localOrder = localItems.map((p) => p.id);

    return JSON.stringify(remoteOrder) !== JSON.stringify(localOrder);
  }, [attachmentsResource.resource, attachments]);

  const updateOrder = async () => {
    await updateAttachmentOrder(
      props.inspection.id,
      attachments.map((p) => p.id),
    );
    await attachmentsResource.reload(props.inspection.id);
  };

  const addInspectionAttachmentsModal = useModal({
    title: "Bijlage(s) toevoegen",
    body: (
      <AddInspectionAttachmentsModal
        inspection={props.inspection}
        onFinished={() => attachmentsResource?.reload(props.inspection.id, true)}
      />
    ),
  });

  return (
    <Card title={"Bijlages"}>
      <Loading loading={attachmentsResource.loading}>
        {hasLocalOrderChanges && (
          <div
            className={"mb-3 border border-amber-500 bg-amber-50 text-amber-900 px-4 py-3 rounded"}
          >
            <p className={"mb-2"}>Je hebt onopgeslagen wijzigingen in de volgorde van de foto's.</p>
            <Button
              type={"primary"}
              size={"sm"}
              text={"Wijzigingen opslaan"}
              onClick={updateOrder}
            />
          </div>
        )}
        <div className={""}>
          {attachments.map((attachment, i) => {
            const movePrev =
              i === 0
                ? undefined
                : () => {
                    setAttachments((old) =>
                      old.map((p, j) => {
                        return j === i - 1 ? attachment : j === i ? old[i - 1] : p;
                      }),
                    );
                  };
            const moveNext =
              i === attachments.length - 1
                ? undefined
                : () => {
                    setAttachments((old) =>
                      old.map((p, j) => (j === i + 1 ? attachment : j === i ? old[i + 1] : p)),
                    );
                  };
            const moveToFront =
              i === 0
                ? undefined
                : () => {
                    setAttachments((old) => {
                      const newOrder = old.filter((_, j) => j !== i);
                      newOrder.unshift(attachment);
                      return newOrder;
                    });
                  };
            const moveToBack =
              i === attachments.length - 1
                ? undefined
                : () => {
                    setAttachments((old) => {
                      const newOrder = old.filter((_, j) => j !== i);
                      newOrder.push(attachment);
                      return newOrder;
                    });
                  };
            const onUpdate = (data: Pick<InspectionAttachment, "name">) => {
              setAttachments((old) =>
                old.map((p) => {
                  if (attachment.id === p.id) {
                    return {
                      ...p,
                      ...data,
                    };
                  } else {
                    return p;
                  }
                }),
              );
            };
            const onDelete = () => {
              setAttachments((old) => old.filter((v) => v.id !== attachment.id));
              attachmentsResource.reload(props.inspection.id, true);
            };

            return (
              <InspectionAttachmentOverviewLine
                key={i}
                position={i + 1}
                attachment={attachment}
                prev={movePrev}
                next={moveNext}
                toFront={moveToFront}
                toBack={moveToBack}
                update={onUpdate}
                delete={onDelete}
                reload={async () => {
                  await attachmentsResource.reload(attachment.inspectionId);
                }}
              />
            );
          })}

          {defaultAttachments.map((defaultAttachment, i) => {
            // Doorlopende nummering: standaardbijlages komen altijd na de inspectiebijlages.
            return (
              <DefaultInspectionAttachmentOverviewLine
                key={defaultAttachment.id}
                position={attachments.length + i + 1}
                attachment={defaultAttachment}
              />
            );
          })}
        </div>
        {hasLocalOrderChanges && (
          <div
            className={"mt-3 border border-amber-500 bg-amber-50 text-amber-900 px-4 py-3 rounded"}
          >
            <p className={"mb-2"}>
              Je hebt onopgeslagen wijzigingen in de volgorde van de bijlages.
            </p>
            <Button
              type={"primary"}
              size={"sm"}
              text={"Wijzigingen opslaan"}
              onClick={updateOrder}
            />
          </div>
        )}
      </Loading>
      <Button
        type={"primary"}
        icon={faUpload}
        text={"Bijlage(s) uploaden"}
        size={"md"}
        onClick={() => addInspectionAttachmentsModal.open()}
      />
    </Card>
  );
};

const InspectionAttachmentOverviewLine: FC<{
  attachment: InspectionAttachment;
  position: number;
  prev?: () => void;
  next?: () => void;
  toFront?: () => void;
  toBack?: () => void;
  update: (attachment: Pick<InspectionAttachment, "name">) => void;
  delete: () => void;
  reload: () => Promise<void>;
}> = (props) => {
  const attachmentNameModal = useModal({
    title: "Naam",
    body: <EditAttachmentName attachment={props.attachment} onFinished={(p) => props.update(p)} />,
  });
  const attachmentDeleteModal = useModal({
    title: "Verwijderen",
    body: <DeleteAttachmentModal attachment={props.attachment} onFinished={() => props.delete()} />,
  });

  return (
    <div
      className={"mb-4 border border-slate-300 rounded-xl px-2 py-2 flex items-center space-x-2"}
    >
      <div
        className={
          "h-8 w-8 border border-brand-600 rounded-full flex items-center justify-center text-xs text-brand-600"
        }
      >
        {props.position}
      </div>
      <IconButton
        icon={faAnglesUp}
        type={"secondary"}
        size={"xs"}
        disabled={!props.toFront}
        onClick={() => props.toFront?.()}
      />
      <IconButton
        icon={faArrowUp}
        type={"secondary"}
        size={"xs"}
        disabled={!props.prev}
        onClick={() => props.prev?.()}
      />
      <IconButton
        icon={faArrowDown}
        type={"secondary"}
        size={"xs"}
        disabled={!props.next}
        onClick={() => props.next?.()}
      />
      <IconButton
        icon={faAnglesDown}
        type={"secondary"}
        size={"xs"}
        disabled={!props.toBack}
        onClick={() => props.toBack?.()}
      />
      <div className={"text-sm flex-1"}>{props.attachment.name}</div>
      <div className={"flex items-center justify-between"}>
        <Button
          type={"secondary"}
          size={"xs"}
          icon={faPencil}
          text={"Hernoemen"}
          onClick={() => attachmentNameModal.open()}
        />
        <Button
          type={"danger"}
          size={"xs"}
          icon={faTrash}
          text={"Verwijderen"}
          onClick={() => attachmentDeleteModal.open()}
        />
      </div>
    </div>
  );
};

const DefaultInspectionAttachmentOverviewLine: FC<{
  attachment: DefaultInspectionAttachment;
  position: number;
}> = (props) => {
  const navigate = useNavigate();
  return (
    <div
      className={"mb-4 border border-slate-300 rounded-xl px-2 py-2 flex items-center space-x-2"}
    >
      <div
        className={
          "h-8 w-8 border border-brand-600 rounded-full flex items-center justify-center text-xs text-brand-600"
        }
      >
        {props.position}
      </div>
      <div className={"text-sm flex-1 flex flex-row gap-4 items-center"}>
        <div>{props.attachment.name}</div>
        <Badge style={BadgeStyle.info}>Standaard</Badge>
      </div>
      <div className={"flex items-center justify-between"}>
        <Button
          type={"secondary"}
          size={"xs"}
          icon={faPencil}
          text={"Instellingen"}
          onClick={() => navigate(`/settings/attachments`)}
        />
      </div>
    </div>
  );
};
